
  /**
   * Pre-Task Safety Plan Component
   * ===============================
   * 
   * Individual form component 
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<PreTaskSafetyPlan> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (jobDetails, jobAnalysis, permitsHazards, hazardEvaluation, crewSignatures)
   * - Implements dynamic hazard control management (up to 7 hazard-control pairs)
   * - Provides comprehensive safety planning with job analysis and hazard identification
   * - Implements checkbox groups for permits, hazard conditions, and control methods
   * - Implements form validation for safety compliance and planning requirements
   * - Implements step validation with signature requirements for foreperson
   * - Flattens nested form structure for backend submission
   * 
   * Unique Complex Logic:
   * - Dynamic hazard control field management with add/remove functionality (up to 7 pairs)
   * - Conditional validation for hazard control fields that become required only when corresponding hazard fields have values
   * - Dynamic crew member management with add/remove functionality (up to 3 additional members)
   * - Real-time validation updates for hazard control fields with subscription management
   * - Boolean array tracking for dynamic UI rendering of hazard controls and crew members
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */

  import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { PreTaskSafetyPlan } from '../../../../../models/forms/pre-task-safety-plan/pre-task-safety-plan.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { WeeklySiteInspection } from '../../../../../models/forms/weekly-site-inspection/weekly-site-inspection.model';

@Component({
  selector: 'app-pre-task-safety-plan',
  templateUrl: './pre-task-safety-plan.component.html',
  styleUrls: ['./pre-task-safety-plan.component.css']
})
export class PreTaskSafetyPlanComponent extends BaseFormComponent<PreTaskSafetyPlan> implements OnInit, OnDestroy {
  override formType = PreTaskSafetyPlan.getFormInfo().formType;
  override model = new PreTaskSafetyPlan();

  private hazardSubscriptions: Subscription[] = [];

  requiredPermitsCheckboxes: { name: string, label: string }[] = [];
  hazardConditionsCheckboxes: { name: string, label: string }[] = [];
  controlMethodsCheckboxes: { name: string, label: string }[] = [];


  crewMemberCount: number = 0;
  showAddCrewMemberButton: boolean = true;
  showCrewMemberIndex: Array<boolean> = [false, false, false];

  hazardControlCount: number = 1;
  showAddHazardControlButton: boolean = true;
  showHazardControlIndex: Array<boolean> = [true, false, false, false, false, false, false];

  // Define the form steps and their properties
  override steps: FormStep[] = PreTaskSafetyPlan.getFormSteps();

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);

    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }


 /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
 protected override initializeForm(): void {
  // Initialize checkbox groupings
  this.requiredPermitsCheckboxes = this.getRequiredPermitsCheckboxes();
  this.hazardConditionsCheckboxes = this.getHazardConditionsCheckboxes();
  this.controlMethodsCheckboxes = this.getControlMethodsCheckboxes();

  // Create checkbox form groups
  const requiredPermitsGroup = this.fb.group({});
  this.requiredPermitsCheckboxes.forEach(checkbox => {
    requiredPermitsGroup.addControl(checkbox.name, this.fb.control(this.model[checkbox.name as keyof PreTaskSafetyPlan] || false));
  });

  const hazardConditionsGroup = this.fb.group({});
  this.hazardConditionsCheckboxes.forEach(checkbox => {
    hazardConditionsGroup.addControl(checkbox.name, this.fb.control(this.model[checkbox.name as keyof PreTaskSafetyPlan] || false));
  });

  const controlMethodsGroup = this.fb.group({});
  this.controlMethodsCheckboxes.forEach(checkbox => {
    controlMethodsGroup.addControl(checkbox.name, this.fb.control(this.model[checkbox.name as keyof PreTaskSafetyPlan] || false));
  });

  this.form = this.fb.group({
    jobDetails: this.fb.group({
      subcontractor: ["MacDonald-Miller Facility Solutions"],
      jobName: [this.model.jobName],
      jobNumber: [this.model.jobNumber],
      taskDescription: [this.model.taskDescription, Validators.required],
      date: [this.model.date, Validators.required]
    }),
    jobAnalysis: this.fb.group({
      jobSteps: [this.model.jobSteps, Validators.required],
 
      identifiedHazard1: [this.model.identifiedHazard1, Validators.required],
      identifiedHazard2: [this.model.identifiedHazard2],
      identifiedHazard3: [this.model.identifiedHazard3],
      identifiedHazard4: [this.model.identifiedHazard4],
      identifiedHazard5: [this.model.identifiedHazard5],
      identifiedHazard6: [this.model.identifiedHazard6],
      identifiedHazard7: [this.model.identifiedHazard7],
   

      hazardControl1: [this.model.hazardControl1, Validators.required],
      hazardControl2: [this.model.hazardControl2],
      hazardControl3: [this.model.hazardControl3],
      hazardControl4: [this.model.hazardControl4],
      hazardControl5: [this.model.hazardControl5],
      hazardControl6: [this.model.hazardControl6],
      hazardControl7: [this.model.hazardControl7],

    }),
    permitsHazards: this.fb.group({
      requiredPermits: requiredPermitsGroup,
      otherPermitDescription: [this.model.otherPermitDescription],
      fireExplosionToxicHazard: [this.model.fireExplosionToxicHazard],
      sdsReviewRequired: [this.model.sdsReviewRequired]
    }),
    hazardEvaluation: this.fb.group({
      hazardConditions: hazardConditionsGroup,
      otherConditions: [this.model.otherConditions],
      controlMethods: controlMethodsGroup,
      otherControlMethod: [this.model.otherControlMethod]
    }),
    crewSignatures: this.fb.group({
      forepersonFirstName: [this.model.forepersonFirstName, Validators.required],
      forepersonLastName: [this.model.forepersonLastName, Validators.required],
      crewMember1FirstName: [this.model.crewMember1FirstName],
      crewMember1LastName: [this.model.crewMember1LastName],
      crewMember2FirstName: [this.model.crewMember2FirstName],
      crewMember2LastName: [this.model.crewMember2LastName],
      crewMember3FirstName: [this.model.crewMember3FirstName],
      crewMember3LastName: [this.model.crewMember3LastName],
      otherNotes: [this.model.otherNotes]
    })
  });

  // Set up dynamic validation for hazard control fields
  this.setupHazardControlValidation();
}

   // Static methods to get the checkbox groupings for the component
 getRequiredPermitsCheckboxes(): { name: string, label: string }[] {
    return [
        { name: 'confinedSpacePermit', label: this.fieldMappings['confinedSpacePermit'] },
        { name: 'hotWorkPermit', label: this.fieldMappings['hotWorkPermit'] },
        { name: 'lockoutTagoutPermit', label: this.fieldMappings['lockoutTagoutPermit'] },
        { name: 'otherPermit', label: this.fieldMappings['otherPermit'] }
    ];
}

getHazardConditionsCheckboxes(): { name: string, label: string }[] {
    return [
        { name: 'contactingTemperatureExtremes', label: this.fieldMappings['contactingTemperatureExtremes'] },
        { name: 'contactingElectricalCurrent', label: this.fieldMappings['contactingElectricalCurrent'] },
        { name: 'environmentalAirborneRelease', label: this.fieldMappings['environmentalAirborneRelease'] },
        { name: 'movingObjectsEquipment', label: this.fieldMappings['movingObjectsEquipment'] },
        { name: 'hazardousSubstance', label: this.fieldMappings['hazardousSubstance'] },
        { name: 'obstructionInterference', label: this.fieldMappings['obstructionInterference'] },
        { name: 'struckBy', label: this.fieldMappings['struckBy'] },
        { name: 'fallSlipTrip', label: this.fieldMappings['fallSlipTrip'] },
        { name: 'materialHandling', label: this.fieldMappings['materialHandling'] },
        { name: 'stuckAgainst', label: this.fieldMappings['stuckAgainst'] },
        { name: 'caughtInBetween', label: this.fieldMappings['caughtInBetween'] },
        { name: 'otherHazardConditions', label: this.fieldMappings['otherHazardConditions'] }
    ];
}

getControlMethodsCheckboxes(): { name: string, label: string }[] {
    return [
        { name: 'ventilationOfExposureArea', label: this.fieldMappings['ventilationOfExposureArea'] },
        { name: 'isolationOfHazardFromWorker', label: this.fieldMappings['isolationOfHazardFromWorker'] },
        { name: 'substitutionOfHazardWithLessSevereOne', label: this.fieldMappings['substitutionOfHazardWithLessSevereOne'] },
        { name: 'eliminationOfHazard', label: this.fieldMappings['eliminationOfHazard'] },
        { name: 'changeOfWorkMethods', label: this.fieldMappings['changeOfWorkMethods'] },
        { name: 'goodWorkPractices', label: this.fieldMappings['goodWorkPractices'] },
        { name: 'personalProtectiveEquipment', label: this.fieldMappings['personalProtectiveEquipment'] },
        { name: 'otherControlMethods', label: this.fieldMappings['otherControlMethods'] }
    ];
}



  // Form group getters
  get jobDetailsFormGroup(): FormGroup {
    return this.form.get('jobDetails') as FormGroup;
  }

  get jobAnalysisFormGroup(): FormGroup {
    return this.form.get('jobAnalysis') as FormGroup;
  }

  get permitsHazardsFormGroup(): FormGroup {
    return this.form.get('permitsHazards') as FormGroup;
  }

  get requiredPermitsFormGroup(): FormGroup {
    return this.permitsHazardsFormGroup.get('requiredPermits') as FormGroup;
  }

  get hazardEvaluationFormGroup(): FormGroup {
    return this.form.get('hazardEvaluation') as FormGroup;
  }

  get hazardConditionsFormGroup(): FormGroup {
    return this.hazardEvaluationFormGroup.get('hazardConditions') as FormGroup;
  }

  get controlMethodsFormGroup(): FormGroup {
    return this.hazardEvaluationFormGroup.get('controlMethods') as FormGroup;
  }

  get crewSignaturesFormGroup(): FormGroup {
    return this.form.get('crewSignatures') as FormGroup;
  }

  /**
   * Retrieves the current form data and creates a new PreTaskSafetyPlan instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): PreTaskSafetyPlan {
    const formValue = this.form.value;
    
    // Flatten the nested form groups into a single object
    const flattenedData = {
      // Job details
      ...formValue.jobDetails,
      
      // Job analysis - individual fields (no longer arrays)
      ...formValue.jobAnalysis,
      
      // Permits & hazards (flatten checkbox groups)
      ...formValue.permitsHazards.requiredPermits,
      otherPermitDescription: formValue.permitsHazards.otherPermitDescription,
      fireExplosionToxicHazard: formValue.permitsHazards.fireExplosionToxicHazard,
      sdsReviewRequired: formValue.permitsHazards.sdsReviewRequired,
      
      // Hazard evaluation (flatten checkbox groups)
      ...formValue.hazardEvaluation.hazardConditions,
      otherConditions: formValue.hazardEvaluation.otherConditions,
      ...formValue.hazardEvaluation.controlMethods,
      otherControlMethod: formValue.hazardEvaluation.otherControlMethod,
      
      // Crew signatures
      ...formValue.crewSignatures
    };

    const preTaskSafetyPlan = new PreTaskSafetyPlan(flattenedData);
    const updatedPreTaskSafetyPlan = this.updateModelWithAttachments(preTaskSafetyPlan);
    return updatedPreTaskSafetyPlan;
    
   
  }

  /**
   * Sets up validation rules for each step of the pre-task safety plan form.
   * Each step has specific validation requirements that must be met before proceeding:
   * - Job Details: Requires basic job information
   * - Job Analysis: Requires job steps, hazards, and controls
   * - Permits & Hazards: Requires hazard assessment questions
   * - Hazard Evaluation: Optional conditions and methods
   * - Crew Signatures: Requires crew member information and signatures
   * 
   * The validation ensures that:
   * 1. All required fields are filled out
   * 2. Required signatures are collected
   * 3. Form data meets safety and compliance requirements
   */
   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }

   

    const forepersonSignatureIndex: number = 0;
 


    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('job-details', this.jobDetailsFormGroup);
    this.setupStepValidation('job-analysis', this.jobAnalysisFormGroup);
    this.setupStepValidation('permits-hazards', this.permitsHazardsFormGroup);
    this.setupStepValidation('hazard-evaluation', this.hazardEvaluationFormGroup);
    this.setupStepValidation('crew-signatures', this.crewSignaturesFormGroup, {signatureIndex: [forepersonSignatureIndex]});
  }

  ngOnDestroy(): void {
    this.hazardSubscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private setupHazardControlValidation(): void {
    const jobAnalysisGroup = this.jobAnalysisFormGroup;
    
    // Set up subscriptions for hazards 2-13 (hazard 1 is always required)
    for (let i = 2; i <= 7; i++) {
      const hazardField = `identifiedHazard${i}`;
      const controlField = `hazardControl${i}`;
      
      const subscription = jobAnalysisGroup.get(hazardField)!.valueChanges.subscribe(value => {
        const controlControl = jobAnalysisGroup.get(controlField)!;
        
        if (value && value.trim() !== '') {
          // Add required validator if hazard has a value
          controlControl.setValidators([Validators.required]);
        } else {
          // Remove required validator if hazard is empty
          controlControl.clearValidators();
        }
        
        controlControl.updateValueAndValidity();
      });
      
      this.hazardSubscriptions.push(subscription);
    }
  }

  addCrewMember() {

    if (this.crewMemberCount < 3) {
      this.crewMemberCount++;
      const crewMemberCount = this.crewMemberCount++

      this.showCrewMemberIndex[crewMemberCount-1] = true;

      this.crewMemberCount = crewMemberCount;
      if (crewMemberCount === 3) {
        this.showAddCrewMemberButton = false;
      }
    }
  }

  addHazardControl() {

    if (this.hazardControlCount < 7) {
      this.hazardControlCount++;
      const hazardControlCount = this.hazardControlCount++
      this.showHazardControlIndex[hazardControlCount-1] = true;
      this.hazardControlCount = hazardControlCount;
      if (hazardControlCount === 7) {
      this.showAddHazardControlButton = false;
    }
  }
}
}